import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../ui/Avatar';
import { CountdownBadge, RelationshipBadge } from '../ui/Badge';

/**
 * PersonCard Component
 *
 * Premium card design for displaying people with beautiful gradients,
 * micro-interactions, and delightful hover states.
 */

// Generate gradient colors based on name
function getGradientColors(name) {
  const gradients = [
    { from: 'from-coral-400', to: 'to-rose-500', shadow: 'shadow-coral-500/30' },
    { from: 'from-violet-400', to: 'to-purple-500', shadow: 'shadow-violet-500/30' },
    { from: 'from-blue-400', to: 'to-cyan-500', shadow: 'shadow-blue-500/30' },
    { from: 'from-emerald-400', to: 'to-teal-500', shadow: 'shadow-emerald-500/30' },
    { from: 'from-amber-400', to: 'to-orange-500', shadow: 'shadow-amber-500/30' },
    { from: 'from-pink-400', to: 'to-rose-500', shadow: 'shadow-pink-500/30' },
    { from: 'from-indigo-400', to: 'to-blue-500', shadow: 'shadow-indigo-500/30' },
    { from: 'from-fuchsia-400', to: 'to-pink-500', shadow: 'shadow-fuchsia-500/30' },
  ];

  // Simple hash based on name
  const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
}

// Get urgency color
function getUrgencyColor(days) {
  if (days === null) return { bg: 'bg-slate-100', text: 'text-slate-600' };
  if (days <= 3) return { bg: 'bg-red-100', text: 'text-red-600', urgent: true };
  if (days <= 7) return { bg: 'bg-coral-100', text: 'text-coral-600' };
  if (days <= 14) return { bg: 'bg-amber-100', text: 'text-amber-600' };
  if (days <= 30) return { bg: 'bg-gold-100', text: 'text-gold-600' };
  return { bg: 'bg-emerald-100', text: 'text-emerald-600' };
}

// Get relationship emoji
function getRelationshipEmoji(relationship) {
  const emojis = {
    family: '👨‍👩‍👧',
    partner: '💑',
    friend: '👯',
    colleague: '💼',
    mentor: '🎓',
  };
  return emojis[relationship?.toLowerCase()] || '💝';
}

// Calculate days until date
function getDaysUntil(dateString) {
  if (!dateString) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const occasionDate = new Date(dateString);
  occasionDate.setFullYear(today.getFullYear());

  if (occasionDate < today) {
    occasionDate.setFullYear(today.getFullYear() + 1);
  }

  const diffTime = occasionDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function PersonCard({
  person,
  onClick,
  showNextOccasion = true,
  className = '',
}) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick(person);
    } else {
      navigate(`/people/${person.id}`);
    }
  };

  const name = person.recipientName || person.name;
  const daysUntil = getDaysUntil(person.date);
  const gradient = getGradientColors(name);
  const urgency = getUrgencyColor(daysUntil);

  return (
    <div
      className={`
        group relative
        bg-white rounded-2xl
        border-2 transition-all duration-300 ease-out
        ${isHovered ? 'border-slate-200 shadow-xl -translate-y-1' : 'border-slate-100 shadow-md'}
        cursor-pointer overflow-hidden
        ${className}
      `}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Decorative gradient accent */}
      <div className={`
        absolute top-0 left-0 right-0 h-1
        bg-gradient-to-r ${gradient.from} ${gradient.to}
        transition-all duration-300
        ${isHovered ? 'h-2' : 'h-1'}
      `} />

      <div className="p-4 flex items-center gap-4">
        {/* Premium Avatar */}
        <div className={`
          relative transition-transform duration-300
          ${isHovered ? 'scale-105' : ''}
        `}>
          <div className={`
            w-14 h-14 rounded-xl
            bg-gradient-to-br ${gradient.from} ${gradient.to}
            flex items-center justify-center
            text-white text-xl font-bold
            shadow-lg ${gradient.shadow}
            transition-all duration-300
            ${isHovered ? 'rounded-2xl shadow-xl' : ''}
          `}>
            {name?.charAt(0).toUpperCase()}
          </div>
          {/* Relationship indicator */}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center text-xs">
            {getRelationshipEmoji(person.relationship)}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 truncate text-lg">
            {name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {person.relationship && (
              <span className="text-sm text-slate-500 capitalize">
                {person.relationship}
              </span>
            )}
            {person.occasion && (
              <>
                <span className="text-slate-300">•</span>
                <span className="text-sm text-slate-500 capitalize">
                  {person.occasion}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Next occasion countdown */}
        {showNextOccasion && daysUntil !== null && (
          <div className={`
            px-3 py-1.5 rounded-full
            ${urgency.bg} ${urgency.text}
            font-semibold text-sm
            transition-all duration-300
            ${isHovered ? 'scale-105' : ''}
            ${urgency.urgent ? 'animate-pulse-subtle' : ''}
          `}>
            {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
          </div>
        )}

        {/* Chevron */}
        <svg
          className={`
            w-5 h-5 text-slate-400
            transition-all duration-300
            ${isHovered ? 'translate-x-1 text-slate-600' : ''}
          `}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

/**
 * PersonCardCompact - Smaller version for selection lists
 */
export function PersonCardCompact({
  person,
  onClick,
  selected = false,
  className = '',
}) {
  const [isHovered, setIsHovered] = useState(false);
  const name = person.recipientName || person.name;
  const gradient = getGradientColors(name);

  return (
    <button
      onClick={() => onClick?.(person)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        flex items-center gap-3 p-3 rounded-xl w-full text-left
        transition-all duration-200
        ${selected
          ? `bg-gradient-to-r ${gradient.from}/10 ${gradient.to}/10 border-2 border-coral-400`
          : 'bg-white hover:bg-slate-50 border-2 border-slate-100'
        }
        ${isHovered && !selected ? 'border-slate-200' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      <div className={`
        w-10 h-10 rounded-lg
        bg-gradient-to-br ${gradient.from} ${gradient.to}
        flex items-center justify-center
        text-white font-semibold
        shadow-md ${gradient.shadow}
        transition-transform duration-200
        ${isHovered ? 'scale-110' : ''}
      `}>
        {name?.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-800 truncate">
          {name}
        </p>
        {person.relationship && (
          <p className="text-xs text-slate-500 truncate capitalize">
            {person.relationship}
          </p>
        )}
      </div>
      {selected && (
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-coral-400 to-coral-600 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </button>
  );
}

/**
 * PersonList - Grid of person cards with stagger animation
 */
export function PersonList({
  people,
  onPersonClick,
  emptyState,
  loading = false,
  className = '',
}) {
  if (loading) {
    return (
      <div className={`space-y-3 ${className}`}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-md animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-slate-200" />
              <div className="flex-1">
                <div className="h-5 w-32 bg-slate-200 rounded-lg" />
                <div className="h-4 w-24 bg-slate-100 rounded-lg mt-2" />
              </div>
              <div className="h-7 w-20 bg-slate-200 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!people || people.length === 0) {
    return emptyState || null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {people.map((person, index) => (
        <div
          key={person.id}
          className="animate-fade-in-up"
          style={{
            animationDelay: `${index * 0.05}s`,
            animationFillMode: 'forwards',
            opacity: 0,
          }}
        >
          <PersonCard
            person={person}
            onClick={onPersonClick}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * RecentPeopleRow - Horizontal scrolling row with premium avatars
 */
export function RecentPeopleRow({
  people,
  onPersonClick,
  className = '',
}) {
  if (!people || people.length === 0) return null;

  return (
    <div className={`-mx-4 px-4 ${className}`}>
      <div className="flex gap-4 overflow-x-auto pb-3 hide-scrollbar">
        {people.map((person, index) => {
          const name = person.recipientName || person.name;
          const gradient = getGradientColors(name);

          return (
            <button
              key={person.id}
              onClick={() => onPersonClick?.(person)}
              className="flex flex-col items-center gap-2 flex-shrink-0 group animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className={`
                w-14 h-14 rounded-xl
                bg-gradient-to-br ${gradient.from} ${gradient.to}
                flex items-center justify-center
                text-white text-lg font-bold
                shadow-lg ${gradient.shadow}
                transition-all duration-300
                group-hover:scale-110 group-hover:rounded-2xl group-hover:shadow-xl
                group-active:scale-95
              `}>
                {name?.charAt(0).toUpperCase()}
              </div>
              <span className="text-xs text-slate-600 max-w-16 truncate font-medium group-hover:text-coral-600 transition-colors">
                {name?.split(' ')[0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default PersonCard;
